import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { SEOHead } from '@/components/SEO/SEOHead';
import { BlogHero } from '@/components/blog/BlogHero';
import { ArticleCard } from '@/components/blog/ArticleCard';
import { useArticles, useArticlesCount } from '@/hooks/useSupabase';

const ARTICLES_PER_PAGE = 12;

export function BlogPage() {
  const { t: tSeo } = useTranslation('seo');
  const { t, i18n } = useTranslation('blog');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filtres serveur (catégorie + langue)
  const serverFilters = useMemo(() => ({
    lang: currentLocale,
    category: selectedCategory || undefined,
    limit: ARTICLES_PER_PAGE,
    offset: (currentPage - 1) * ARTICLES_PER_PAGE,
  }), [currentLocale, selectedCategory, currentPage]);

  // Compteur total pour la pagination (même filtres sans limit/offset)
  const { data: totalCount } = useArticlesCount({
    lang: currentLocale,
    category: selectedCategory || undefined,
  });

  // Fetch articles avec pagination serveur
  const { data: articles, isLoading } = useArticles(serverFilters);

  // Filtre recherche côté client (le reste est géré côté serveur)
  const filteredArticles = useMemo(() => {
    if (!articles) return [];
    if (!searchTerm) return articles;

    const term = searchTerm.toLowerCase();
    return articles.filter(
      (article) =>
        article.title.toLowerCase().includes(term) ||
        article.excerpt.toLowerCase().includes(term)
    );
  }, [articles, searchTerm]);

  // Reset page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  // Pagination serveur
  const totalPages = Math.ceil((totalCount || 0) / ARTICLES_PER_PAGE);
  // Les articles sont déjà paginés côté serveur, on les affiche directement
  // (sauf si recherche textuelle active, auquel cas on filtre côté client)
  const paginatedArticles = filteredArticles;

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={tSeo('blog.title')}
        description={tSeo('blog.description')}
        path="/blog"
      />
      <div>
        <BlogHero
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        articlesCount={totalCount || 0}
      />

      {/* Why Premium Content */}
      <section className="py-6 bg-gradient-to-br from-aspexit-green/5 via-white to-aspexit-orange/5">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl border-2 border-aspexit-green/20 p-6 shadow-lg">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-aspexit-green/10 flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-7 h-7" xmlns="http://www.w3.org/2000/svg">
                    {/* Document */}
                    <rect x="25" y="15" width="50" height="70" fill="#55BC8C" opacity="0.15" rx="3" />
                    <rect x="25" y="15" width="50" height="70" fill="none" stroke="#55BC8C" strokeWidth="3" rx="3" />

                    {/* Document lines */}
                    <line x1="35" y1="30" x2="60" y2="30" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" />
                    <line x1="35" y1="40" x2="65" y2="40" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" />
                    <line x1="35" y1="50" x2="55" y2="50" stroke="#55BC8C" strokeWidth="2" strokeLinecap="round" />

                    {/* Premium star badge */}
                    <circle cx="65" cy="65" r="18" fill="white" stroke="#55BC8C" strokeWidth="2.5" />
                    <path d="M 65 52 L 68 60 L 76 61 L 70 67 L 72 75 L 65 71 L 58 75 L 60 67 L 54 61 L 62 60 Z" fill="#55BC8C" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-heading font-bold text-aspexit-black mb-3">
                    {t('whyPremium.title')}
                  </h2>
                  <p className="text-sm text-aspexit-gray mb-3 leading-relaxed">
                    {t('whyPremium.description')}
                  </p>
                  <ul className="space-y-2 text-sm text-aspexit-gray">
                    {(t('whyPremium.points', { returnObjects: true }) as string[]).map((point, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-aspexit-green mt-1">✓</span>
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white min-h-screen">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-aspexit-green"></div>
              <p className="text-aspexit-gray mt-4">{t('loading')}</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl font-extrabold text-aspexit-black mb-6">
                {t('noResults.title')}
              </p>
              <p className="text-aspexit-gray mb-8">
                {t('noResults.subtitle')}
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                className="px-6 py-3 bg-aspexit-green text-white rounded-full hover:bg-aspexit-green-dark transition-colors"
              >
                {t('resetFilters')}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {paginatedArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    slug={article.slug}
                    title={article.title}
                    excerpt={article.excerpt}
                    category={article.category}
                    publishedAt={article.published_at || article.created_at}
                    readingTime={article.reading_time}
                    featuredImage={article.featured_image}
                    isPremium={article.is_premium}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-16">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-6 py-2.5 text-aspexit-gray rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                  >
                    {t('pagination.previous')}
                  </button>
                  <div className="flex gap-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Show first page, last page, current page, and pages around current
                      const showPage =
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1);

                      // Show ellipsis
                      const showEllipsisBefore = page === currentPage - 2 && currentPage > 3;
                      const showEllipsisAfter =
                        page === currentPage + 2 && currentPage < totalPages - 2;

                      if (showEllipsisBefore || showEllipsisAfter) {
                        return (
                          <span
                            key={page}
                            className="w-10 h-10 flex items-center justify-center text-aspexit-gray"
                          >
                            ...
                          </span>
                        );
                      }

                      if (!showPage) return null;

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                            page === currentPage
                              ? 'bg-aspexit-green text-white'
                              : 'text-aspexit-gray hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-6 py-2.5 bg-aspexit-green text-white rounded-lg hover:bg-aspexit-green-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('pagination.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      </div>
    </>
  );
}
